const globalStore = {
	state: {
		/**
		 * Keys used to persist data to localStorage
		 */
		localStorageKeys: {
			currentUser: "Lotus:CurrentUser"
		},
		//Page/Component level keys start with "Component:{id==some-name-we-need-to-make-sure-is-not-duplicated-across-components}:{key==whatever-component-level-key-we-want}"


		currentUser: {

			/**
			 * User's authentication token after they login.
			 * Send with every API request.
			 */
			authToken: "",

			/**
			 * Set once the API has told us this session's `authToken` is no good -
			 * expired, revoked, or signed out somewhere else.
			 *
			 * Nothing local can work this out. Portal session tokens are issued with
			 * no expiration date (see the back-end's AuthTokensService.GenerateAuthToken),
			 * so there is no expiry to count down to; a token stops working the moment
			 * the server retires it, which only the server knows. "Still valid" is
			 * therefore whatever the most recent API response said it was.
			 *
			 * Set by api2.js on a "not-authenticated" response. Read by isLoggedIn(),
			 * which reports false from that point on.
			 */
			isAuthTokenKnownInvalid: false,

			userId: "",
			activeAccountId: "",


			// When the user is a client accessing a client portal (e.g. Tessta Client Portal)
			activeClientId: "",
			activeClientDisplayName: "",


			completeAccessStructure: {},

			firstName: "",
			lastName: "",
			displayName: "",
			
			isLotusAccount: false,

			isRememberMe: false,	// Is the user's details being stored to localStorage for future sessions?

			isAliasLogin: false,
			lotusUserId: "",

			// When this session began (ms since epoch, from Date.now()). Set once at
			// login and carried through localStorage on rehydration - never refreshed
			// on page load. Used by /helpers/useMaxSessionDuration.js to enforce
			// config.js's `session.maxDurationMinutes`, when enabled.
			sessionStartedAtUtc: null
		},


		app: {
			completeAccessStructureForUserLastRetrievedUtc: null
		},


		// Path to redirect to after logging in, if coming to the specified page but not yet logged in (ie. the "redirect=" type path)
		redirectAfterLoginPath: "",


		//pageLayoutClass: "container-fluid",
		pageLayoutClass: "container",


		/**
		 * For poping data between components where emitting events doesn't play nicely (e.g. user ID from timesheet back to parent layout then into sidebar components)
		 */
		pipe: {
			timesheetUserId: ""
		},


		systemAdminSelectedAccountId: ""
	},



	localStorage: {

		/**
		 * Save data to localStorage by the specified key.  
		 * Takes any type/structure/value of data and `JSON.stringify` it before saving.
		 * 
		 * @param {string} key The key to save to localStorage.
		 * @param {any} value The data to save to localStorage.
		 * @returns void
		 */
		saveDataToStorage(key, value)
		{
			if (value == null)
			{
				// Remove the item
				localStorage.removeItem(key);
				return;
			}


			// console.log("globalStore.localStorage.saveDataToStorage(): ", key, value);

			var json = this.dataToJson(value);

			localStorage[key] = json;
		},



		dataToJson(data, pretty = false)
		{
			var json = "";

			if (pretty == true)
			{
				json = JSON.stringify(data, null, 3);
			}
			else
			{
				// Minimal for storage
				json = JSON.stringify(data);
			}

			return json;
		},




		/**
		 * Return data from localStorage by the specified key.
		 * 
		 * @param {string} key The key to retrieve from localStorage.
		 * @returns The saved data - already hydrated with `JSON.parse()` - if it exists.  
		 * Otherwise `null`.
		 */
		getDataFromStorage(key)
		{
			var storedData = localStorage[key];

			if (storedData == undefined)
			{
				return null;
			}
			storedData = JSON.parse(storedData);

			return storedData;
		}
	},





	systemAdmin: {
		setSelectedAccountId(accountId)
		{
			globalStore.state.systemAdminSelectedAccountId = accountId;
		}
	},





	currentUser: {
		setCurrentUser(authToken,
			userId,
			firstName,
			lastName,
			displayName,
			activeAccountId,
			activeClientId,
			activeClientDisplayName,
			completeAccessStructure,	//accessProfiles,
			isLotusAccount,
			rememberMe,
			sessionStartedAtUtc = Date.now())
		{
			//console.log("globalStore.currentUser.setCurrentUser(): completeAccessStructure=", completeAccessStructure);


			globalStore.state.currentUser.authToken = authToken;

			// A token we've just been handed is valid until the API says otherwise.
			globalStore.state.currentUser.isAuthTokenKnownInvalid = false;

			globalStore.state.currentUser.userId = userId;
			globalStore.state.currentUser.firstName = firstName;
			globalStore.state.currentUser.lastName = lastName;
			globalStore.state.currentUser.displayName = displayName;
			globalStore.state.currentUser.activeAccountId = activeAccountId;

			globalStore.state.currentUser.activeClientId = activeClientId;
			globalStore.state.currentUser.activeClientDisplayName = activeClientDisplayName;

			globalStore.state.currentUser.isLotusAccount = (isLotusAccount === true ? true : false);	// Terniary in case the value hasn't been previously stored and is undefined.

			globalStore.state.currentUser.completeAccessStructure = completeAccessStructure;

			globalStore.state.currentUser.isRememberMe = rememberMe;

			globalStore.state.currentUser.sessionStartedAtUtc = sessionStartedAtUtc;

			// Save to local storage. NOTE: Check is done within the method to ensure saving is allowed.
			globalStore.currentUser.rememberCurrentUser();

			//console.log("globalStore.currentUser.setCurrentUser(): globalStore.state.currentUser=", globalStore.state.currentUser);
		},


		/**
		 * When a Lotus admin aliases as another user, identify this as an alias session and set the ID of the Lotus user doing the work.
		 * @param {string} lotusUserId User.ID of the Lotus user who is aliasing the current user.
		 */
		setCurrentUserAlias(lotusUserId)
		{
			//console.log("setCurrentUserAlias(): lotusUserId=", lotusUserId);
			
			globalStore.state.currentUser.isAliasLogin = true;
			globalStore.state.currentUser.lotusUserId = lotusUserId;

			// Save to local storage. NOTE: Check is done within the method to ensure saving is allowed.
			globalStore.currentUser.rememberCurrentUser();
		},



		/**
		 * Record that the API has rejected this session's auth token, so every later
		 * isLoggedIn() reports false - including after a reload, since the flag is
		 * persisted alongside the rest of the signed-in user.
		 *
		 * This does not end the session on its own: the caller decides where the
		 * person goes next (normally goToSessionExpired() in /helpers/session.js).
		 */
		markAuthTokenInvalid()
		{
			if (globalStore.state.currentUser.isAuthTokenKnownInvalid == true) return;	// Already known.

			globalStore.state.currentUser.isAuthTokenKnownInvalid = true;

			// Persist directly rather than through rememberCurrentUser(), which only writes for
			// a "remember me" session. A dead token has to survive a reload either way, otherwise
			// the next page load would trust the stored token again and earn another 401.
			globalStore.localStorage.saveDataToStorage(globalStore.state.localStorageKeys.currentUser, globalStore.state.currentUser);
		},



		setCurrentUserCompleteAccessStructure(completeAccessStructure)
		{
			//console.log("globalStore.currentUser.setCurrentUserCompleteAccessStructure(): completeAccessStructure=", completeAccessStructure);

			globalStore.state.currentUser.completeAccessStructure = completeAccessStructure;
			
			// Save to local storage
			globalStore.localStorage.saveDataToStorage(globalStore.state.localStorageKeys.currentUser, globalStore.state.currentUser);
		},


		rememberCurrentUser()
		{
			// Ensure we'r allowed to save data based on the user's remember me selection
			if (globalStore.state.currentUser.isRememberMe == false) return;	// Not remembering


			//NOTE: Obsolete. clearCurrentUser() should be called explicitly where needed to avoid accidental clears.
			// Clear the current user first. Consider this a reset.
			//globalStore.currentUser.clearCurrentUser();

			// Persist to localStorage to remember next load.
			globalStore.localStorage.saveDataToStorage(globalStore.state.localStorageKeys.currentUser, globalStore.state.currentUser);
		},



		resetCurrentUserDataStructure()
		{
			globalStore.state.currentUser.authToken = "";
			globalStore.state.currentUser.isAuthTokenKnownInvalid = false;
			globalStore.state.currentUser.userId = "";
			globalStore.state.currentUser.firstName = "";
			globalStore.state.currentUser.lastName = "";
			globalStore.state.currentUser.displayName = "";
			globalStore.state.currentUser.activeAccountId = "";
			globalStore.state.currentUser.activeClientId = "";
			globalStore.state.currentUser.activeClientDisplayName = "";
			globalStore.state.currentUser.isLotusAccount = false;

			globalStore.state.currentUser.isRememberMe = false;

			globalStore.state.currentUser.isAliasLogin = false;
			globalStore.state.currentUser.lotusUserId = "";

			globalStore.state.currentUser.sessionStartedAtUtc = null;

			globalStore.state.currentUser.completeAccessStructure = {};
		},


		clearCurrentUser()
		{
			console.log("clearCurrentUser()");

			globalStore.currentUser.resetCurrentUserDataStructure();

			globalStore.localStorage.saveDataToStorage(globalStore.state.localStorageKeys.currentUser, null);
		},


		//TODO: Note - this is getting called multiple times (via app.js) for some reason. Be sure to clear arrays before loading each time.
		/**
		 * Load the current user's details from local storage.
		 */
		loadCurrentUser()
		{
			// Get details from storage
			var storedDetails = globalStore.localStorage.getDataFromStorage(globalStore.state.localStorageKeys.currentUser);

			//console.log("globalStore.currentUser.loadCurrentUser(): storedDetails=", storedDetails);

			if (storedDetails == null || storedDetails == undefined)
			{
				// No details currently stored.
				return;
			}

			//TODO: That just
			// Mainly to make sure arrays are cleared if this function is called multiple times om a page/route load.
			globalStore.currentUser.resetCurrentUserDataStructure();

			// Populate the data store.
			// (rememberMe = false because it's already in localStorage)
			globalStore.currentUser.setCurrentUser(storedDetails.authToken,
				storedDetails.userId,
				storedDetails.firstName,
				storedDetails.lastName,
				storedDetails.displayName,
				storedDetails.activeAccountId,
				storedDetails.activeClientId,
				storedDetails.activeClientDisplayName,
				storedDetails.completeAccessStructure,
				storedDetails.isLotusAccount,
				storedDetails.isRememberMe,
				storedDetails.sessionStartedAtUtc);

			if (storedDetails.isAliasLogin)
			{
				globalStore.currentUser.setCurrentUserAlias(storedDetails.lotusUserId);
			}

			// setCurrentUser() above treats the token as good, which is right for a fresh sign-in
			// but not for a rehydration: if the API had already rejected this token before the
			// reload, it is still rejected now.
			if (storedDetails.isAuthTokenKnownInvalid == true)
			{
				globalStore.currentUser.markAuthTokenInvalid();
			}
			
		},


		/**
		 * Is there a signed-in user whose auth token we have no reason to doubt?
		 *
		 * This is deliberately synchronous - it's called from a router guard and from
		 * computed properties - so it can't ask the API whether the token is still
		 * good. It answers from what's already known:
		 *
		 *  * a user ID			someone signed in on this browser;
		 *  * an auth token		we have something to prove it to the API with;
		 *  * not yet rejected	the API hasn't since told us that token is dead.
		 *
		 * The last of those is the closest a synchronous check can get to "still
		 * valid", and it is not a prediction: portal tokens carry no expiry date, so
		 * the only way a client learns a token has stopped working is by being told.
		 * api2.js records that verdict centrally (see markAuthTokenInvalid), which is
		 * what makes it available here.
		 *
		 * A `true` therefore means "no reason to think otherwise", not "confirmed with
		 * the server a moment ago". When a caller needs the stronger guarantee, it has
		 * to ask the API - app.js's checkCurrentAuthTokenStillValid() does exactly that
		 * on start-up.
		 */
		isLoggedIn()
		{
			if (globalStore.state.currentUser.userId == "") return false;					// Nobody signed in.
			if (globalStore.state.currentUser.authToken == "") return false;				// Signed in, but nothing to authenticate with.
			if (globalStore.state.currentUser.isAuthTokenKnownInvalid == true) return false;	// The API has since rejected the token.

			return true;
		},



		hasFeatureAccess(featureCode)
		{
			// if (!globalStore.state.currentUser.completeAccessStructure) return false;

			// Default to false.
			var result = false;

			// Data structure not creatd yet.
			if (!globalStore.state.currentUser.completeAccessStructure) return false;
			if (!globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags) return false;

			//console.log("hasFeatureAccess(" + featureCode + "): globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags=", globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags);

			globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags.forEach( flag => {
				// console.log("flag for '" + featureCode + "': ", flag);
				if (flag.FeatureCode.toLowerCase() == featureCode.toLowerCase())
				{
					//console.log("found!!!!", flag);

					//TODO: 2022-04-24: "Granted" is replacing "Enabled". "Enabled" is retained for the transition period.
					if (flag.Access == "Enabled" || flag.Access == "Granted" || flag.Access == "AccountGrantAllUsers") 
					{
						result = true;
						return;	// Exit the forEach()
					}

					// Otherwise Access == "Disabled"
					return;	// Exit the forEach. `result` will remain the default false.
				}
			});

			
			return result;

		},



		/**
		 * Is the CRM module switch (Admin > CRM & Sales, account setting "CRM.Enabled") on for the active account?
		 * Separate from the CRM.Enabled feature flag; the CRM menu needs both. Comes with the access structure, so a
		 * change shows within a minute (the structure's refresh interval) or at the next login.
		 */
		isCrmWorkspaceOn()
		{
			return globalStore.state.currentUser.completeAccessStructure?.IsCrmWorkspaceOn === true;
		},



		/**
		 * Record a CRM switch change just saved by this user, so the menu follows it at once rather than at the next
		 * access-structure refresh (which would set the same value from the server).
		 */
		setCrmWorkspaceOn(isOn)
		{
			if (!globalStore.state.currentUser.completeAccessStructure) return;

			globalStore.state.currentUser.completeAccessStructure.IsCrmWorkspaceOn = isOn === true;
			globalStore.localStorage.saveDataToStorage(globalStore.state.localStorageKeys.currentUser, globalStore.state.currentUser);
		},


	},





	helpers: {

		/**
		 * Returns a JavaScript date representing today (for the user's browser date).  
		 * Could be manually set to a specific date for testing locally.
		 */
		getTodayDate()
		{
			return new Date();
			//return new Date(2021, 10, 16);	// 16 Nov 2021 (remember moth is zero based for some absurd fucking reasons only the idiot who designed Date() willl ever know)
		},



		/**
		 * Does the user's Account have a feature - `featureCode` - enabled?
		 * NOTE: this is derived from `globalStore.currentUser.hasFeatureAccess(featureCode)` [above].
		 */
		hasAccountFeature(featureFlagsSet, featureCode)
		{
			// if (!globalStore.state.currentUser.completeAccessStructure) return false;

			// console.log(">>>>>> hasAccountFeature(): featureFlagsSet=", featureFlagsSet);

			// Default to false.
			var result = false;

			if (!featureFlagsSet) return false;

			//console.log("hasFeatureAccess(" + featureCode + "): globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags=", globalStore.state.currentUser.completeAccessStructure.FeatureAccessFlags);

			featureFlagsSet.forEach( flag => {
				// console.log("flag for '" + featureCode + "': ", flag);
				if (flag.FeatureCode.toLowerCase() == featureCode.toLowerCase())
				{
					//console.log("found!!!!", flag);

					//TODO: 2022-04-24: "Granted" is replacing "Enabled". "Enabled" is retained for the transition period.
					if (flag.Access == "Enabled" || flag.Access == "Granted") 
					{
						result = true;
						return;	// Exit the forEach()
					}

					// Otherwise Access == "Disabled"
					return;	// Exit the forEach. `result` will remain the default false.
				}
			});

			
			return result;

		},
	}
};



export default globalStore;